import React from 'react';
import {
    Table,
    Paper,
    Badge,
    ActionIcon,
    Tooltip,
    Box,
    Text,
    Tabs,
    Alert,
    SimpleGrid,
    Checkbox,
    Group,
    Stack,
} from '@mantine/core';
import {
    IconEye,
    IconEdit,
    IconPencil,
    IconTrash,
    IconTable as TableRowsIcon,
    IconCode as CodeIcon,
    IconArrowUp as ArrowUpwardIcon,
    IconArrowDown as ArrowDownwardIcon,
    IconAlertTriangle as WarningAmberIcon
} from '@tabler/icons-react';
import { getBindingViolationMessage, type BindingConstraints } from '../../types/EntityEntitlements';
import { getReferenceDisplay } from '../../types/ReferenceTypes';
import { getPluralizedLabel } from '../../utils/pluralizationUtils';
import FeatureFlagsService from '../../services/FeatureFlagsService';
import { debugService } from '../../services/DebugService';
import { debugLog } from '../../utils/debugLog';
import { TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from './EmptyState';
import { useMantineColorScheme } from '@mantine/core';
import AceEditor from 'react-ace';
import 'ace-builds';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';

export interface TableColumn<T> {
    key: string;
    label: string;
    width?: string | number;
    align?: 'left' | 'right' | 'center';
    render?: (item: T, index: number) => React.ReactNode;
    sortable?: boolean;
    title?: string;
}

export interface TableAction<T> {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: (item: T, event: React.MouseEvent) => void;
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
    disabled?: (item: T) => boolean;
}

export interface SortState {
    column: string;
    direction: 'asc' | 'desc';
}

export interface ListResponse<T = any> {
    items: T[];
    totalResults: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    [key: string]: any;
}

export interface GenericTableProps<T> {
    data: T[];
    columns: TableColumn<T>[];
    isLoading?: boolean;
    loadingRows?: number;
    onRowClick?: (item: T) => void;
    getRowKey: (item: T) => string;
    elevation?: number;
    emptyMessage?: string;
    hover?: boolean;
    selectable?: boolean;
    selectedItems?: T[];
    onSelectionChange?: (items: T[]) => void;
    entityType?: string;
    rowErrors?: Map<string, string>;
    onClearRowError?: (rowKey: string) => void;
    sortState?: SortState | null;
    onSortChange?: (sortState: SortState | null) => void;
    isFullscreen?: boolean;
    listResponse?: ListResponse<T> | null;
    showEditIcon?: boolean;
    onEditClick?: (item: T) => void;
    onTabChange?: (tabIndex: number) => void;
    showBindingWarnings?: boolean;
    bindingConstraintsMap?: Record<string, BindingConstraints>;
    /** When true, hide the Raw/$ tabs and show only the Data view. */
    hideDebugTabs?: boolean;
}

export function GenericTable<T>({
    data,
    columns,
    isLoading = false,
    loadingRows: _loadingRows = 5,
    onRowClick,
    getRowKey,
    elevation: _elevation = 2,
    emptyMessage = "No data available",
    hover = true,
    selectable = false,
    selectedItems = [],
    onSelectionChange,
    entityType,
    rowErrors,
    onClearRowError,
    sortState,
    onSortChange,
    isFullscreen = false,
    listResponse,
    showEditIcon = false,
    onEditClick,
    onTabChange,
    showBindingWarnings = true,
    bindingConstraintsMap,
    hideDebugTabs = false
}: GenericTableProps<T>) {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [activeTab, setActiveTab] = React.useState<string | null>('data');
    const featureFlags = React.useMemo(() => FeatureFlagsService.getInstance(), []);
    const sortingEnabled = featureFlags.isEnabled('tableSorting');

    const tabIndexMap: Record<string, number> = { data: 0, raw: 1, dollar: 2 };

    const lowerEntityType = entityType?.toLowerCase() || '';
    const isJournalEntity = lowerEntityType === 'journal' ||
        lowerEntityType === 'journalentry' ||
        lowerEntityType === 'journal entry' ||
        lowerEntityType.replace(/\s+/g, '') === 'journalentry';

    const hasLoggedRef = React.useRef(false);
    React.useEffect(() => {
        if (!hasLoggedRef.current && sortingEnabled) {
            debugLog(`GenericTable: tableSorting feature flag enabled: ${sortingEnabled}, has onSortChange: ${!!onSortChange}, has sortState: ${!!sortState}`);
            hasLoggedRef.current = true;
        }
    }, [sortingEnabled, onSortChange, sortState]);

    const handleRowClick = (item: T) => {
        if (onRowClick) {
            onRowClick(item);
        }
    };

    const generateEntityRoute = async (item: T): Promise<string | null> => {
        if (!entityType || !onRowClick) return null;
        const entity = item as any;
        const entityId = entity?.Id || entity?.id;
        if (!entityId) return null;
        const normalizedEntityType = entityType.toLowerCase();

        if (normalizedEntityType === 'group') return `#groups/view/${entityId}`;
        else if (normalizedEntityType === 'class') return `#classes/view/${entityId}`;

        try {
            const { EntityCategorizationService } = await import('../../services/EntityCategorizationService');
            const categorizationService = EntityCategorizationService.getInstance();
            const category = await categorizationService.getEntityCategory(normalizedEntityType);
            if (category.type === 'person') return `#Person/${normalizedEntityType}/view/${entityId}`;
            else if (category.type === 'locality') return `#localities/${normalizedEntityType}/view/${entityId}`;
            else if (category.type === 'admin') return `#admin/${normalizedEntityType}/view/${entityId}`;
            else return `#other/${normalizedEntityType}/view/${entityId}`;
        } catch (error) {
            console.error('Error generating route for middle-click:', error);
            if (['set', 'policyrule', 'journalentry', 'modificationerror'].includes(normalizedEntityType)) {
                return `#admin/${normalizedEntityType}/view/${entityId}`;
            }
            return `#other/${normalizedEntityType}/view/${entityId}`;
        }
    };

    const handleRowMouseDown = async (e: React.MouseEvent, item: T) => {
        if (e.button === 1 && onRowClick) {
            e.preventDefault();
            e.stopPropagation();
            const route = await generateEntityRoute(item);
            if (route) window.open(route, '_blank');
        }
    };

    const isSelected = (item: T) => {
        return selectedItems.some(selected => getRowKey(selected) === getRowKey(item));
    };

    const selectionEnabled = selectable && typeof onSelectionChange === 'function';
    const selectableRowKeys = React.useMemo(() => {
        if (!selectionEnabled) return [];
        return data.map((item) => getRowKey(item)).filter(Boolean);
    }, [selectionEnabled, data, getRowKey]);

    const selectedKeySet = React.useMemo(() => {
        if (!selectionEnabled) return new Set<string>();
        return new Set(selectedItems.map((item) => getRowKey(item)));
    }, [selectionEnabled, selectedItems, getRowKey]);

    const visibleSelectedCount = React.useMemo(() => {
        if (!selectionEnabled) return 0;
        let count = 0;
        for (const k of selectableRowKeys) {
            if (selectedKeySet.has(k)) count++;
        }
        return count;
    }, [selectionEnabled, selectableRowKeys, selectedKeySet]);

    const allVisibleSelected = selectionEnabled && selectableRowKeys.length > 0 && visibleSelectedCount === selectableRowKeys.length;
    const someVisibleSelected = selectionEnabled && visibleSelectedCount > 0 && visibleSelectedCount < selectableRowKeys.length;

    const handleSelectAllVisible = (checked: boolean) => {
        if (!selectionEnabled) return;
        if (!checked) {
            // Deselect only visible items; keep any non-visible selections (future-proof if reused elsewhere)
            const next = selectedItems.filter((item) => !selectableRowKeys.includes(getRowKey(item)));
            onSelectionChange?.(next);
            return;
        }
        const selectedByKey = new Map<string, T>();
        for (const item of selectedItems) selectedByKey.set(getRowKey(item), item);
        for (const item of data) selectedByKey.set(getRowKey(item), item);
        onSelectionChange?.(Array.from(selectedByKey.values()));
    };

    const isDisplayNameColumn = (columnKey: string): boolean => {
        const normalizedKey = columnKey.toLowerCase();
        return normalizedKey === 'name' || normalizedKey === 'displayname';
    };
    const isSameSortColumn = (left?: string | null, right?: string | null): boolean => {
        if (!left || !right) return false;
        return left.toLowerCase() === right.toLowerCase();
    };

    const handleSort = (columnKey: string) => {
        if (!sortingEnabled || !onSortChange) return;
        const column = columns.find(col => col.key === columnKey);
        if (column?.sortable === false) return;

        const effectiveSortState = sortState || (isDisplayNameColumn(columnKey) ? { column: columnKey, direction: 'asc' as const } : null);
        const isCurrentColumnSorted = effectiveSortState != null && isSameSortColumn(effectiveSortState.column, columnKey);

        if (isCurrentColumnSorted) {
            if (effectiveSortState.direction === 'asc') {
                onSortChange({ column: columnKey, direction: 'desc' });
            } else {
                if (isDisplayNameColumn(columnKey)) onSortChange({ column: columnKey, direction: 'asc' });
                else onSortChange(null);
            }
        } else {
            onSortChange({ column: columnKey, direction: 'asc' });
        }
    };

    const handleSelectRow = (item: T) => {
        if (!selectable || !onSelectionChange) return;
        const isItemSelected = isSelected(item);
        if (isItemSelected) {
            onSelectionChange(selectedItems.filter(selected => getRowKey(selected) !== getRowKey(item)));
        } else {
            onSelectionChange([...selectedItems, item]);
        }
    };

    const handleTabChange = (value: string | null) => {
        setActiveTab(value);
        if (value) onTabChange?.(tabIndexMap[value] ?? 0);
    };

    React.useEffect(() => {
        if (activeTab) onTabChange?.(tabIndexMap[activeTab] ?? 0);
    }, [activeTab, onTabChange]);

    const renderRawTableView = () => {
        if (isLoading) {
            return (
                <Box className="hades-table-loader" style={{ minHeight: 300 }}>
                    <Box className="hades-loader-dots">
                        <span /><span /><span />
                    </Box>
                    <Text size="sm" c="dimmed" mt="sm">{loadingLabel}</Text>
                </Box>
            );
        }
        if (data.length === 0) {
            return <EmptyState title={emptyMessage} />;
        }
        return (
            <Box p="md">
                <Text size="lg" fw={600} mb="sm">Raw Data ({data.length} items)</Text>
                <Box mah={600} style={{ overflow: 'auto' }}>
                    {data.map((item, index) => (
                        <Box key={getRowKey(item)} mb="md">
                            <Text
                                size="sm"
                                fw={500}
                                c="blue"
                                mb={4}
                                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                onClick={() => onRowClick && handleRowClick(item)}
                            >
                                Item {index + 1} - ID: {getRowKey(item)}
                                {onRowClick && <Text component="span" ml="sm" fz="0.8em" opacity={0.7}>(Click to view details)</Text>}
                            </Text>
                            <Paper
                                p="sm"
                                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                onClick={() => onRowClick && handleRowClick(item)}
                            >
                                <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(item, null, 2)}
                                </pre>
                            </Paper>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    };

    const renderCell = (column: TableColumn<T>, item: T, index: number) => {
        const value = (item as any)[column.key];
        let content: React.ReactNode;

        if (column.render) {
            content = column.render(item, index);
        } else {
            if (value === null || value === undefined) content = '-';
            else if (typeof value === 'string') content = value;
            else if (typeof value === 'number' || typeof value === 'boolean') content = String(value);
            else if (Array.isArray(value)) {
                if (value.length === 0) content = '-';
                else if (typeof value[0] === 'object') {
                    if (value[0].display) content = `${value.length} items (${value[0].display}...)`;
                    else if (value[0].DisplayName) content = `${value.length} items (${value[0].DisplayName}...)`;
                    else content = `${value.length} items`;
                } else content = value.join(', ');
            } else if (typeof value === 'object') {
                const displayText = getReferenceDisplay(value);
                if (displayText) content = displayText;
                else {
                    console.warn('GenericTable: Attempting to render object directly:', { column: column.key, value, item });
                    content = '[Object]';
                }
            } else content = String(value);
        }

        if (showBindingWarnings && bindingConstraintsMap) {
            const constraints = bindingConstraintsMap[column.key] ?? bindingConstraintsMap[column.key.toLowerCase()];
            const violation = constraints ? getBindingViolationMessage(value, constraints) : null;
            if (violation) {
                content = (
                    <Group gap={4} wrap="nowrap" style={{ display: 'inline-flex' }}>
                        {content}
                        <Tooltip label={violation}>
                            <WarningAmberIcon size={16} style={{ color: 'var(--mantine-color-yellow-6)' }} />
                        </Tooltip>
                    </Group>
                );
            }
        }

        return content;
    };

    const hasEditColumn = Boolean(showEditIcon && onEditClick);
    const hasSelectColumn = selectionEnabled;
    const colSpanAll = columns.length + (hasEditColumn ? 1 : 0) + (hasSelectColumn ? 1 : 0);

    const ACTION_COL_WIDTH = 36;
    const ACTION_COL_PADDING = '4px 4px';

    const loadingLabel = entityType
        ? `Loading ${entityType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}s…`
        : 'Loading data…';

    const renderLoadingRows = () => (
        <Table.Tr>
            <Table.Td colSpan={colSpanAll} style={{ border: 'none' }}>
                <Box className="hades-table-loader">
                    <Box className="hades-loader-dots">
                        <span /><span /><span />
                    </Box>
                    <Text size="sm" c="dimmed" mt="sm">{loadingLabel}</Text>
                </Box>
            </Table.Td>
        </Table.Tr>
    );

    const renderDataRows = () => {
        if (data.length === 0 && !isLoading) {
            return (
                <Table.Tr>
                    <Table.Td colSpan={colSpanAll}>
                        <EmptyState title={emptyMessage} />
                    </Table.Td>
                </Table.Tr>
            );
        }

        return data.map((item, index) => {
            const rowKey = getRowKey(item);
            const uniqueKey = `${rowKey}-${index}`;
            const errorMessage = rowErrors?.get(rowKey);

            return (
                <React.Fragment key={uniqueKey}>
                    <Table.Tr
                        data-row-key={rowKey}
                        onClick={() => handleRowClick(item)}
                        onMouseDown={(e: React.MouseEvent) => handleRowMouseDown(e, item)}
                        style={{
                            cursor: onRowClick ? 'pointer' : 'default',
                            backgroundColor: isSelected(item) ? 'var(--mantine-color-brand-light)' : undefined,
                        }}
                    >
                        {hasSelectColumn && (
                            <Table.Td
                                w={ACTION_COL_WIDTH}
                                p={ACTION_COL_PADDING}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                <Checkbox
                                    aria-label="Select row"
                                    checked={isSelected(item)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => handleSelectRow(item)}
                                />
                            </Table.Td>
                        )}
                        {hasEditColumn && (
                            <Table.Td
                                w={ACTION_COL_WIDTH}
                                p={ACTION_COL_PADDING}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                <Tooltip label="Edit">
                                    <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="blue"
                                        aria-label="Edit"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            onEditClick?.(item);
                                        }}
                                    >
                                        <IconPencil size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </Table.Td>
                        )}
                        {columns.map((column) => (
                            <Table.Td
                                key={column.key}
                                data-column-key={column.key}
                                data-column-label={column.label}
                                style={{
                                    textAlign: column.align || 'left',
                                    width: column.width,
                                    maxWidth: column.width || 300,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {renderCell(column, item, index)}
                            </Table.Td>
                        ))}
                    </Table.Tr>
                    {errorMessage && (
                        <Table.Tr>
                            <Table.Td colSpan={colSpanAll} py={4} px="xs">
                                <Alert color="red" withCloseButton onClose={() => onClearRowError?.(rowKey)}>
                                    {errorMessage}
                                </Alert>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </React.Fragment>
            );
        });
    };

    const renderTableView = () => (
        <Paper shadow="xs">
            <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                <Table highlightOnHover={hover} striped="odd" withTableBorder stickyHeader>
                    <Table.Thead>
                        <Table.Tr>
                        {hasSelectColumn && (
                            <Table.Th w={ACTION_COL_WIDTH}>
                                <Checkbox
                                    aria-label="Select all visible rows"
                                    checked={allVisibleSelected}
                                    indeterminate={someVisibleSelected}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleSelectAllVisible(e.currentTarget.checked);
                                    }}
                                />
                            </Table.Th>
                        )}
                            {hasEditColumn && <Table.Th w={ACTION_COL_WIDTH} />}
                            {columns.map((column) => {
                                const isSortable = !isJournalEntity && sortingEnabled && !!onSortChange && (column.sortable !== false);
                                const isDisplayNameCol = isDisplayNameColumn(column.key);
                                const effectiveSortState = sortState || (isDisplayNameCol ? { column: column.key, direction: 'asc' as const } : null);
                                const isSorted = isSameSortColumn(effectiveSortState?.column, column.key);
                                const sortDirection = isSorted ? (effectiveSortState?.direction ?? null) : null;

                                return (
                                    <Table.Th
                                        key={column.key}
                                        data-column-key={column.key}
                                        data-column-label={column.label}
                                        title={column.title}
                                        onClick={isSortable ? () => handleSort(column.key) : undefined}
                                        style={{
                                            textAlign: column.align || 'left',
                                            width: column.width,
                                            cursor: isSortable ? 'pointer' : 'default',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Group gap={4} wrap="nowrap">
                                            {column.label}
                                            {isSortable && (
                                                <Stack gap={0} ml={2}>
                                                    <ArrowUpwardIcon
                                                        size={14}
                                                        style={{
                                                            opacity: sortDirection === 'asc' ? 1 : 0.3,
                                                            color: sortDirection === 'asc' ? 'var(--mantine-color-brand-6)' : 'inherit',
                                                        }}
                                                    />
                                                    <ArrowDownwardIcon
                                                        size={14}
                                                        style={{
                                                            marginTop: -6,
                                                            opacity: sortDirection === 'desc' ? 1 : 0.3,
                                                            color: sortDirection === 'desc' ? 'var(--mantine-color-brand-6)' : 'inherit',
                                                        }}
                                                    />
                                                </Stack>
                                            )}
                                        </Group>
                                    </Table.Th>
                                );
                            })}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {isLoading ? renderLoadingRows() : renderDataRows()}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        </Paper>
    );

    const hasDollarOrUnderscoreKeys = listResponse && typeof listResponse === 'object' && Object.keys(listResponse).some((k) => k.startsWith('$') || k.startsWith('_'));
    const showDollarTab = listResponse && typeof listResponse === 'object' && (hasDollarOrUnderscoreKeys || debugService.isEnabled());

    return (
        <Box>
            <Paper shadow="xs">
                {!isFullscreen && !hideDebugTabs && (
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tabs.List>
                            <Tabs.Tab value="data" leftSection={<TableRowsIcon size={16} />}>Data</Tabs.Tab>
                            <Tabs.Tab value="raw" leftSection={<CodeIcon size={16} />}>Raw</Tabs.Tab>
                            {showDollarTab && <Tabs.Tab value="dollar">$</Tabs.Tab>}
                        </Tabs.List>
                    </Tabs>
                )}

                <Box mt={isFullscreen ? 0 : 'sm'}>
                    {activeTab === 'data' && renderTableView()}
                    {activeTab === 'raw' && renderRawTableView()}
                    {showDollarTab && activeTab === 'dollar' && (() => {
                        const dollarEntries = listResponse && typeof listResponse === 'object'
                            ? Object.entries(listResponse).filter(([key]) => key.startsWith('$'))
                            : [];
                        const underscoreEntries = listResponse && typeof listResponse === 'object'
                            ? Object.entries(listResponse).filter(([key]) => key.startsWith('_'))
                            : [];
                        const metaEntries = [...dollarEntries, ...underscoreEntries];
                        if (metaEntries.length === 0) {
                            return (
                                <Box p="md">
                                    <Text size="sm" c="dimmed">
                                        No list-level $ or _ data returned for this request. Enable debug and retry, or check the API response shape.
                                    </Text>
                                </Box>
                            );
                        }
                        const debugQuery = listResponse?.$debug?.Query != null
                            ? String(listResponse.$debug.Query)
                            : null;
                        const aceTheme = isDark ? 'monokai' : 'github';
                        return (
                            <Box mt="sm">
                                {debugQuery != null && (
                                    <Paper p="md" mb="md">
                                        <Text size="sm" c="dimmed" fw={600} mb="xs">$debug.Query</Text>
                                        <AceEditor
                                            mode="sql"
                                            theme={aceTheme}
                                            value={debugQuery}
                                            readOnly
                                            name="dollar-debug-query-sql"
                                            editorProps={{ $blockScrolling: true }}
                                            width="100%"
                                            height="320px"
                                            fontSize={13}
                                            showPrintMargin={false}
                                            showGutter
                                            highlightActiveLine={false}
                                            setOptions={{ showLineNumbers: true, tabSize: 2, useWorker: false, showFoldWidgets: true }}
                                        />
                                    </Paper>
                                )}
                                <Text size="sm" c="dimmed" mb="xs">
                                    $ properties are server-side; _ properties are client-side.
                                </Text>
                                <Text size="md" c="dimmed" fw={500} mb="sm">
                                    List response properties whose keys start with $ or _
                                </Text>
                                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                    {metaEntries.map(([key, value]) => {
                                        const isComplexObject = typeof value === 'object' && value !== null;
                                        const isDebugWithQuery = key === '$debug' && isComplexObject && (value as any).Query != null;
                                        const jsonValue = isDebugWithQuery
                                            ? (() => { const { Query, ...rest } = value as any; return JSON.stringify(rest, null, 2); })()
                                            : (isComplexObject ? JSON.stringify(value, null, 2) : null);
                                        return (
                                            <Paper key={key} p="sm" withBorder style={isComplexObject ? { gridColumn: '1 / -1' } : undefined}>
                                                <Text size="xs" c="dimmed" mb={4}>
                                                    {key}
                                                    {isDebugWithQuery && ' (Query shown in SQL viewer above)'}
                                                </Text>
                                                {typeof value === 'boolean' ? (
                                                    <Checkbox checked={value} readOnly size="sm" />
                                                ) : jsonValue != null ? (
                                                    <AceEditor
                                                        mode="json"
                                                        theme={aceTheme}
                                                        value={jsonValue}
                                                        readOnly
                                                        name={`dollar-json-${key}`}
                                                        editorProps={{ $blockScrolling: true }}
                                                        width="100%"
                                                        height={`${Math.min(400, 120 + (jsonValue.split('\n').length * 18))}px`}
                                                        fontSize={12}
                                                        showPrintMargin={false}
                                                        showGutter
                                                        highlightActiveLine={false}
                                                        setOptions={{ showLineNumbers: true, tabSize: 2, useWorker: false, showFoldWidgets: true }}
                                                    />
                                                ) : (
                                                    <Text size="sm" style={{
                                                        fontFamily: typeof value === 'string' && (String(value).includes('{') || String(value).includes('[')) ? 'monospace' : 'inherit',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        {String(value ?? 'N/A')}
                                                    </Text>
                                                )}
                                            </Paper>
                                        );
                                    })}
                                </SimpleGrid>
                            </Box>
                        );
                    })()}
                </Box>
            </Paper>
        </Box>
    );
}
